/** API Feed Data: the read-only pdn_inbox view (GET /integrations/feed-inbox). */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { PageMeta } from "@/components/app/data-table";
import { api } from "@/lib/api-client";

export const FEED_SOURCES = ["scapture", "cob", "pdn", "lotgen"] as const;
export type FeedSource = (typeof FEED_SOURCES)[number];
export const FEED_STATUSES = [
  "arrival_pending",
  "arrival_done",
  "cancelled",
] as const;
export type FeedStatus = (typeof FEED_STATUSES)[number];

/** The page's tabs: the sources that have ever sent a row (nothing ever wrote Core PDN). */
export const FEED_TABS = ["scapture", "cob", "lotgen"] as const;
export type FeedTab = (typeof FEED_TABS)[number];

export const FEED_SOURCE_LABELS: Record<FeedSource, string> = {
  scapture: "S-Capture",
  cob: "S-Capture COB",
  pdn: "Core PDN",
  lotgen: "Lotgen PDN",
};
export const FEED_STATUS_LABELS: Record<FeedStatus, string> = {
  arrival_pending: "Arrival pending",
  arrival_done: "Arrival done",
  cancelled: "Cancelled",
};

export interface FeedRow {
  id: number;
  source: FeedSource;
  status: FeedStatus;
  pdnNo: string;
  dispatchId: string | null;
  coreLotNumber: string;
  srn: string | null;
  date: string | null;
  dispatchDate: string | null;
  crop: string | null;
  spMale: string | null;
  spFemale: string | null;
  declaredQty: string | null;
  declaredBags: number | null;
  moisturePct: string | null;
  moistureStatus: string | null;
  gotStatus: string | null;
  productionGrade: string | null;
  destination: string | null;
  vehicle: string | null;
  arrivalDocumentId: number | null;
  arrivalDocNo: string | null;
  arrivalFormCode: string | null;
  coreSyncStatus: string;
  createdAt: string;
}

export interface FeedDetail extends Omit<FeedRow, "vehicle"> {
  harvestDate: string | null;
  productionType: string | null;
  transport: Record<string, unknown> | null;
  bags: unknown[] | null;
  farmer: Record<string, unknown> | null;
  lotgen: Record<string, unknown> | null;
  raw: Record<string, unknown> | null;
  lotgenCheckedAt: string | null;
  syncedAt: string | null;
  updatedAt: string;
}

export interface FeedListParams {
  source?: FeedSource | undefined;
  status?: FeedStatus | undefined;
  q?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page?: number | undefined;
}

export interface FeedListResponse {
  items: FeedRow[];
  meta: PageMeta;
  counts: Record<"all" | FeedSource, number>;
  /** The last Lotgen sync (every 15 minutes, by the worker). */
  lotgenSync: { at: string | null; status: string | null };
}

export function useFeedList(p: FeedListParams) {
  const qs = new URLSearchParams({ page: String(p.page ?? 1), limit: "50" });
  for (const k of ["source", "status", "q", "from", "to"] as const) {
    const v = p[k];
    if (v) qs.set(k, v);
  }
  return useQuery({
    queryKey: ["feed-inbox", p],
    queryFn: () => api<FeedListResponse>(`/integrations/feed-inbox?${qs}`),
    placeholderData: keepPreviousData,
  });
}

export function useFeedDetail(id: number | null) {
  return useQuery({
    queryKey: ["feed-inbox", "row", id],
    queryFn: () => api<FeedDetail>(`/integrations/feed-inbox/${id}`),
    enabled: id !== null,
  });
}

/**
 * One Lotgen lot read live (GET /integrations/feed-lotgen/:lotNo), for a number the table does not
 * hold: a lot from the old Lotgen register is never synced.
 */
export interface LotgenFeedRow {
  lotNo: string;
  pdnNo: string | null;
  date: string | null;
  dispatchDate: string | null;
  crop: string | null;
  variety: string | null;
  spMale: string | null;
  spFemale: string | null;
  declaredQty: string | null;
  declaredBags: number | null;
  moisturePct: string | null;
  moistureStatus: string | null;
  gotStatus: string | null;
  arrivalDocumentId: number | null;
  arrivalDocNo: string | null;
  arrivalFormCode: string | null;
}

export interface LotgenFeedLot extends LotgenFeedRow {
  harvestDate: string | null;
  farmer: Record<string, unknown> | null;
  remarks: string | null;
  raw: Record<string, unknown> | null;
}

/** One lot, whole: the new Lotgen app, then the old register (lots from before it). */
export function useLotgenFeedLot(lotNo: string | null) {
  return useQuery({
    queryKey: ["feed-lotgen", "lot", lotNo],
    queryFn: () =>
      api<LotgenFeedLot>(
        `/integrations/feed-lotgen/${encodeURIComponent(lotNo!)}`
      ),
    enabled: lotNo !== null,
    retry: false,
  });
}
